const DEFAULT_PREFLIGHT_TIMEOUT_MS = 3000;
const DEFAULT_PRINT_TIMEOUT_MS = 15000;

const FAILURE_MESSAGES = Object.freeze({
  offline: 'Não foi possível acessar o CIBD. Verifique se o CIBD está aberto e permita o acesso à rede/dispositivos locais no navegador.',
  timeout: 'O CIBD não respondeu no tempo esperado. Tente novamente.',
  http: 'O conector de impressão não concluiu a solicitação. Tente novamente.',
});

export class CibdClientError extends Error {
  constructor(kind, { phase, status } = {}) {
    const safeKind = Object.prototype.hasOwnProperty.call(FAILURE_MESSAGES, kind) ? kind : 'offline';
    super(FAILURE_MESSAGES[safeKind]);
    this.name = 'CibdClientError';
    this.kind = safeKind;
    this.phase = phase === 'print' ? 'print' : 'preflight';
    if (Number.isInteger(status)) this.status = status;
  }
}

const cleanBaseUrl = (baseUrl) => String(baseUrl || '').replace(/\/+$/, '');

const positiveTimeout = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const requestWithTimeout = async ({
  fetchImpl,
  url,
  options,
  phase,
  timeoutMs,
  AbortControllerImpl,
}) => {
  const Controller = AbortControllerImpl
    || (typeof AbortController === 'function' ? AbortController : null);
  const controller = Controller ? new Controller() : null;
  let timedOut = false;
  let timeoutId;

  const timeout = new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      timedOut = true;
      if (controller) controller.abort();
      reject(new CibdClientError('timeout', { phase }));
    }, timeoutMs);
  });

  const request = Promise.resolve().then(() => fetchImpl(url, {
    ...options,
    ...(controller ? { signal: controller.signal } : {}),
  }));

  try {
    return await Promise.race([request, timeout]);
  } catch (error) {
    if (error instanceof CibdClientError) throw error;
    if (timedOut || error?.name === 'AbortError') {
      throw new CibdClientError('timeout', { phase });
    }
    throw new CibdClientError('offline', { phase });
  } finally {
    clearTimeout(timeoutId);
  }
};

const ensureOk = (response, phase) => {
  if (response?.ok) return response;
  const status = Number.isInteger(response?.status) ? response.status : undefined;
  throw new CibdClientError('http', { phase, status });
};

const readJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

export const checkCibdStatus = async ({
  baseUrl,
  timeoutMs = DEFAULT_PREFLIGHT_TIMEOUT_MS,
  fetchImpl = fetch,
  AbortControllerImpl,
} = {}) => {
  const response = await requestWithTimeout({
    fetchImpl,
    url: `${cleanBaseUrl(baseUrl)}/api/public/status`,
    options: {
      method: 'GET',
      credentials: 'omit',
      cache: 'no-store',
    },
    phase: 'preflight',
    timeoutMs: positiveTimeout(timeoutMs, DEFAULT_PREFLIGHT_TIMEOUT_MS),
    AbortControllerImpl,
  });

  ensureOk(response, 'preflight');
  return readJson(response);
};

export const sendCibdPrint = async ({
  baseUrl,
  headers,
  body,
  printTimeoutMs = DEFAULT_PRINT_TIMEOUT_MS,
  fetchImpl = fetch,
  AbortControllerImpl,
} = {}) => {
  const response = await requestWithTimeout({
    fetchImpl,
    url: `${cleanBaseUrl(baseUrl)}/api/system/print/comanda`,
    options: {
      method: 'POST',
      headers,
      credentials: 'omit',
      body,
    },
    phase: 'print',
    timeoutMs: positiveTimeout(printTimeoutMs, DEFAULT_PRINT_TIMEOUT_MS),
    AbortControllerImpl,
  });

  ensureOk(response, 'print');
  return readJson(response);
};

export const CIBD_TIMEOUTS = Object.freeze({
  preflight: DEFAULT_PREFLIGHT_TIMEOUT_MS,
  print: DEFAULT_PRINT_TIMEOUT_MS,
});
